// /components/mymap.web.js
import React, { useEffect, useRef } from 'react';
import { View, StyleSheet } from 'react-native';
import markerPhone from '../../assets/images/marker-phone.png';
import { Asset } from 'expo-asset';

// Google Maps API Key
const GOOGLE_MAPS_API_KEY = 'AIzaSyCdFGHSm2PGHB5ZNDC6uPxzYGHACCwlcbQ';
// Map ID required for Advanced Markers
// Can be overridden via EXPO_PUBLIC_GOOGLE_MAPS_MAP_ID environment variable
const GOOGLE_MAPS_MAP_ID = process.env.EXPO_PUBLIC_GOOGLE_MAPS_MAP_ID || 'a1949eda607095f8eef5f713';
// Use Advanced Markers when Map ID is available
const USE_ADVANCED_MARKERS = !!GOOGLE_MAPS_MAP_ID;

const MAPS_READY_POLL_MS = 50;
const MAPS_READY_TIMEOUT_MS = 15000;

const isGoogleMapsReady = () => typeof window.google?.maps?.Map === 'function';

// Load Google Maps script.
// With `loading=async` the script's onload fires before `google.maps.Map` exists,
// so readiness is polled until the constructor is available or the timeout expires.
// `onError` is called once when the script fails or never becomes ready.
const loadGoogleMapsScript = (callback, onError) => {
  if (isGoogleMapsReady()) {
    callback();
    return () => {};
  }

  let settled = false;
  let pollId = null;
  let timeoutId = null;
  const settle = (error) => {
    if (settled) return;
    settled = true;
    clearInterval(pollId);
    clearTimeout(timeoutId);
    if (error) {
      console.error(error.message);
      onError?.(error);
    } else {
      callback();
    }
  };

  let script = document.querySelector(`script[src*="maps.googleapis.com"]`);
  if (!script) {
    script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,marker&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (isGoogleMapsReady()) settle();
    };
    document.head.appendChild(script);
  }

  const failedScript = script;
  const previousOnError = failedScript.onerror;
  failedScript.onerror = (event) => {
    previousOnError?.(event);
    // Drop the failed tag so a retry can insert a fresh one.
    failedScript.parentNode?.removeChild(failedScript);
    settle(new Error('Failed to load Google Maps script'));
  };

  pollId = setInterval(() => {
    if (isGoogleMapsReady()) settle();
  }, MAPS_READY_POLL_MS);
  timeoutId = setTimeout(() => {
    if (isGoogleMapsReady()) {
      settle();
    } else {
      settle(new Error('Timed out waiting for Google Maps to initialize'));
    }
  }, MAPS_READY_TIMEOUT_MS);

  return () => {
    settled = true;
    clearInterval(pollId);
    clearTimeout(timeoutId);
  };
};

// Helper function to create circle marker element for AdvancedMarkerElement
const createCircleMarkerElement = (fillColor, strokeColor) => {
  const element = document.createElement('div');
  element.style.width = '16px';
  element.style.height = '16px';
  element.style.borderRadius = '50%';
  element.style.backgroundColor = fillColor;
  element.style.border = `2px solid ${strokeColor}`;
  element.style.boxSizing = 'border-box';
  return element;
};

// A no-op Marker component for web; markers are handled in MapView
export const Marker = () => null;

const MapView = ({ style, region, showsUserLocation, markers = [], onMapReady, onMapError }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRefs = useRef([]);
  const userLocationMarkerRef = useRef(null);
  const containerElementRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const initializedRef = useRef(false);
  const lastDimensionsRef = useRef({ width: 0, height: 0 });
  const resizeRafRef = useRef(null);
  const lastRegionRef = useRef(null);
  const hasReportedReadyRef = useRef(false);

  // Callback ref to get the actual DOM element from React Native Web View
  const setContainerRef = (element) => {
    containerElementRef.current = element;
  };

  // Separate effect for map initialization
  useEffect(() => {
    if (!mapRef.current) return;

    // Get container element (simplified logic)
    const getContainerElement = () => {
      let container = containerElementRef.current;
      if (container?._nativeNode) {
        container = container._nativeNode;
      }
      if (!container || typeof container.getBoundingClientRect !== 'function') {
        container = mapRef.current?.parentElement;
      }
      return container;
    };

    // Ensure map div has explicit dimensions.
    //
    // WebKit (iOS Safari / Chrome iOS) fires ResizeObserver callbacks
    // synchronously after layout, unlike Blink which batches them into a
    // microtask. Writing `style.width`/`style.height` inside the callback
    // can therefore re-enter the observer on the same frame, and subpixel
    // rounding differences between iOS Safari and our `Math.round` guard
    // let dimensions oscillate by 1px in alternate iterations. The result
    // is an unbounded recursion that blows WebKit's smaller stack limit
    // (~6k frames vs Chrome's ~20k), surfacing as
    // "RangeError: Maximum call stack size exceeded" (ALLDRIVERS-WEB-5,
    // 100% Chrome Mobile iOS on /dashboard/home).
    //
    // To break the loop we:
    //   1. rAF-debounce the whole callback so we never run more than once
    //      per frame (sync re-entry from WebKit is collapsed away).
    //   2. Disconnect the observer before mutating the map div, and
    //      re-observe on the next frame after layout has settled. This
    //      guarantees the mutation itself can never schedule another
    //      callback.
    const updateMapDimensions = () => {
      if (!mapRef.current) return;
      if (resizeRafRef.current) return; // rAF already pending — coalesce
      resizeRafRef.current = requestAnimationFrame(() => {
        resizeRafRef.current = null;
        if (!mapRef.current) return;

        const containerElement = getContainerElement();
        if (!containerElement || typeof containerElement.getBoundingClientRect !== 'function') {
          return;
        }

        const containerRect = containerElement.getBoundingClientRect();
        const width = Math.round(containerRect.width);
        const height = Math.round(containerRect.height);
        if (width <= 0 || height <= 0) return;

        const last = lastDimensionsRef.current;
        if (last.width === width && last.height === height) return;
        lastDimensionsRef.current = { width, height };

        // Detach the observer across the DOM write so WebKit cannot
        // re-enter this callback synchronously.
        const observer = resizeObserverRef.current;
        observer?.disconnect();

        mapRef.current.style.width = `${width}px`;
        mapRef.current.style.height = `${height}px`;

        if (mapInstanceRef.current && window.google?.maps?.event) {
          window.google.maps.event.trigger(mapInstanceRef.current, 'resize');
        }

        // Re-attach on the next frame, after the layout we just kicked
        // off has been committed.
        if (observer && containerElement) {
          requestAnimationFrame(() => {
            if (resizeObserverRef.current === observer) {
              observer.observe(containerElement);
            }
          });
        }
      });
    };

    // Only initialize once
    if (initializedRef.current) return;

    // Start loading Google Maps script immediately (async, won't block)
    const cancelScriptLoad = loadGoogleMapsScript(() => {
      try {
        if (!mapInstanceRef.current && mapRef.current && window.google?.maps?.Map) {
          initializedRef.current = true;
          
          // Ensure dimensions are set right before initializing map
          updateMapDimensions();
          
          const center = region ? 
            { lat: region.latitude, lng: region.longitude } : 
            { lat: 38.7223, lng: -9.1393 }; // Default to Lisbon if no region

          // Map initialized with center
          mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
            center: center,
            zoom: 15,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            mapId: GOOGLE_MAPS_MAP_ID, // Required for Advanced Markers
          });
          
          // Set up ResizeObserver after map is initialized
          const containerElement = getContainerElement();
          if (window.ResizeObserver && containerElement && typeof containerElement.getBoundingClientRect === 'function') {
            resizeObserverRef.current = new ResizeObserver(updateMapDimensions);
            resizeObserverRef.current.observe(containerElement);
          }
          
          // Listen for map ready event and trigger final resize
          if (mapInstanceRef.current) {
            window.google.maps.event.addListenerOnce(mapInstanceRef.current, 'idle', () => {
              // Map is ready, ensure proper sizing one final time
              updateMapDimensions();
              if (!hasReportedReadyRef.current) {
                hasReportedReadyRef.current = true;
                onMapReady?.();
              }
            });
          }
        }
      } catch (error) {
        console.error('Error initializing map:', error);
        initializedRef.current = false; // Allow retry on error
        onMapError?.(error);
      }
    }, (error) => onMapError?.(error));

    return () => {
      cancelScriptLoad();
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      if (resizeRafRef.current) {
        cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = null;
      }
    };
  }, []); // Initialize only once on mount

  // Separate effect for region updates
  useEffect(() => {
    if (!region || !mapInstanceRef.current) return;

    const nextRegion = {
      latitude: Number(region.latitude),
      longitude: Number(region.longitude),
    };

    if (!Number.isFinite(nextRegion.latitude) || !Number.isFinite(nextRegion.longitude)) {
      return;
    }

    const lastRegion = lastRegionRef.current;
    const sameRegion =
      !!lastRegion &&
      Math.abs(lastRegion.latitude - nextRegion.latitude) < 0.00001 &&
      Math.abs(lastRegion.longitude - nextRegion.longitude) < 0.00001;

    if (sameRegion) {
      return;
    }

    lastRegionRef.current = nextRegion;

    loadGoogleMapsScript(() => {
      try {
        if (mapInstanceRef.current && mapInstanceRef.current.getCenter) {
          mapInstanceRef.current.panTo({
            lat: nextRegion.latitude,
            lng: nextRegion.longitude,
          });

          const currentZoom = typeof mapInstanceRef.current.getZoom === 'function'
            ? mapInstanceRef.current.getZoom()
            : null;
          if (currentZoom !== 15) {
            mapInstanceRef.current.setZoom(15);
          }
        }
      } catch (error) {
        console.error('Error updating map region:', error);
      }
    });
  }, [region]);

  // Separate effect for user location marker
  useEffect(() => {
    if (!showsUserLocation || !mapInstanceRef.current) return;

    loadGoogleMapsScript(() => {
      try {
        // Verify map is ready - check for Marker (AdvancedMarkerElement only if Map ID provided)
        if (!mapInstanceRef.current || !window.google?.maps?.Marker) return;
        
        // Remove existing user location marker if it exists
        if (userLocationMarkerRef.current) {
          if (userLocationMarkerRef.current.map !== undefined) {
            // AdvancedMarkerElement
            userLocationMarkerRef.current.map = null;
          } else if (userLocationMarkerRef.current.setMap) {
            // Legacy Marker
            userLocationMarkerRef.current.setMap(null);
          }
          userLocationMarkerRef.current = null;
        }
        
        // Use region location if available
        if (region && mapInstanceRef.current) {
          const userLocation = {
            lat: region.latitude,
            lng: region.longitude,
          };
          // Placing user location marker
          
          // Use AdvancedMarkerElement if available and Map ID is set, fallback to Marker
          if (USE_ADVANCED_MARKERS && window.google?.maps?.marker?.AdvancedMarkerElement) {
            userLocationMarkerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
              position: userLocation,
              map: mapInstanceRef.current,
              content: createCircleMarkerElement('#4285F4', '#FFFFFF'),
            });
          } else {
            // Fallback to legacy Marker
            userLocationMarkerRef.current = new window.google.maps.Marker({
              position: userLocation,
              map: mapInstanceRef.current,
              icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 8,
                fillColor: '#4285F4',
                fillOpacity: 1,
                strokeColor: '#FFFFFF',
                strokeWeight: 2,
              },
            });
          }
        }
      } catch (error) {
        console.error('Error placing user location marker:', error);
      }
    });
  }, [region, showsUserLocation]);

  // Separate effect for custom markers
  useEffect(() => {
    if (!mapInstanceRef.current || markers.length === 0) return;

    loadGoogleMapsScript(() => {
      try {
        // Verify map is ready
        if (!mapInstanceRef.current || !window.google?.maps?.Marker || !mapInstanceRef.current.getCenter) {
          return;
        }
        
        // Load custom marker icon asset and add markers
        Asset.fromModule(markerPhone).downloadAsync().then(asset => {
          const iconUrl = asset.localUri || asset.uri;
          // Clear previous markers
          markerRefs.current.forEach(mk => {
            if (mk) {
              if (mk.map !== undefined) {
                // AdvancedMarkerElement
                mk.map = null;
              } else if (mk.setMap) {
                // Legacy Marker
                mk.setMap(null);
              }
            }
          });
          markerRefs.current = [];
          // Add markers for each driver using the custom icon
          markers.forEach(m => {
            if (m.location && m.location.latitude && m.location.longitude) {
              let marker;
              
              // Use AdvancedMarkerElement if available and Map ID is set, fallback to Marker
              if (USE_ADVANCED_MARKERS && window.google?.maps?.marker?.AdvancedMarkerElement) {
                const markerElement = document.createElement('img');
                markerElement.src = iconUrl;
                markerElement.style.width = '32px';
                markerElement.style.height = '32px';
                
                marker = new window.google.maps.marker.AdvancedMarkerElement({
                  position: { lat: m.location.latitude, lng: m.location.longitude },
                  map: mapInstanceRef.current,
                  content: markerElement,
                });
              } else {
                // Fallback to legacy Marker
                marker = new window.google.maps.Marker({
                  position: { lat: m.location.latitude, lng: m.location.longitude },
                  map: mapInstanceRef.current,
                  icon: {
                    url: iconUrl,
                    scaledSize: new window.google.maps.Size(32, 32),
                  },
                });
              }
              
              markerRefs.current.push(marker);
            }
          });
          // Fit map to show all markers
          if (markers.length > 0 && mapInstanceRef.current && markerRefs.current.length > 0) {
            try {
              const bounds = new window.google.maps.LatLngBounds();
              let hasValidBounds = false;
              markerRefs.current.forEach(marker => {
                if (marker) {
                  let position;
                  if (marker.position) {
                    // AdvancedMarkerElement - position is a LatLng object
                    position = marker.position;
                  } else if (marker.getPosition) {
                    // Legacy Marker
                    position = marker.getPosition();
                  }
                  if (position) {
                    // Handle both LatLng objects and LatLngLiteral
                    if (typeof position.lat === 'function') {
                      // LatLng object
                      bounds.extend(position);
                    } else {
                      // LatLngLiteral {lat, lng}
                      bounds.extend(new window.google.maps.LatLng(position.lat, position.lng));
                    }
                    hasValidBounds = true;
                  }
                }
              });
              if (hasValidBounds) {
                mapInstanceRef.current.fitBounds(bounds);
              }
            } catch (error) {
              console.error('Error fitting bounds:', error);
            }
          }
        }).catch(err => {
          console.error('Error loading marker icon asset:', err);
        });
      } catch (error) {
        console.error('Error handling markers:', error);
      }
    });
  }, [markers]);

  return (
    <View ref={setContainerRef} style={[styles.container, style]}>
      <div ref={mapRef} style={styles.map} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    width: '100%',
    minHeight: 400, // Minimum height for cases without flex
  },
  map: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
  },
});

export default MapView;